import React, { useRef, useState, useEffect } from 'react';
import { Box, Flex, HStack, Text } from '@mybridge/ui';

// Custom SVG Icons to avoid external icon library dependency issues
const PlayIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 5V19L19 12L8 5Z" fill="white" />
  </svg>
);

const PauseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 19H10V5H6V19ZM14 5V19H18V5H14Z" fill="white" />
  </svg>
);

const MuteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.21.05-.42.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" fill="white" />
  </svg>
);

const VolumeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" fill="white" />
  </svg>
);

const FullscreenIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" fill="white" />
  </svg>
);

export const ChatVideoPlayer = ({
  src,
  mimeType = 'video/mp4',
  isSender = false,
  width = '200px',
  height = '200px',
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Border radius based on sender vs receiver
  const borderRadius = isSender ? '20px 0 20px 20px' : '0 20px 20px 20px';

  // Toggle video playback
  const togglePlay = (e) => {
    if (e) e.stopPropagation();
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play().catch(err => console.error("Playback failed", err));
      setIsPlaying(true);
    }
  };

  // Toggle mute
  const toggleMute = (e) => {
    if (e) e.stopPropagation();
    if (!videoRef.current) return;

    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  // Request Fullscreen
  const toggleFullscreen = (e) => {
    if (e) e.stopPropagation();
    if (!videoRef.current) return;

    if (videoRef.current.requestFullscreen) {
      videoRef.current.requestFullscreen();
    } else if (videoRef.current.webkitRequestFullscreen) { /* Safari */
      videoRef.current.webkitRequestFullscreen();
    } else if (videoRef.current.msRequestFullscreen) { /* IE11 */
      videoRef.current.msRequestFullscreen();
    }
  };

  // Update current time during playback
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  // Load duration when metadata is ready
  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      if (videoRef.current.currentTime === 0) {
        videoRef.current.currentTime = 0.001;
      }
    }
  };

  // Format seconds to M:SS
  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds) || !isFinite(timeInSeconds)) return '0:00';
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Handle when video ends
  const handleVideoEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  return (
    <Box
      ref={containerRef}
      position="relative"
      width={width}
      height={height}
      minH={'180px'}
      border={isSender ? '2px solid #ecf3fe' : '2px solid #EFF3F4'}
      borderRadius={borderRadius}
      overflow="hidden"
      bg="black"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      cursor="pointer"
      onClick={togglePlay}
      boxShadow="sm"
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        src={src}
        preload="metadata"
        playsInline
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          backgroundColor: '#000',
        }}
        onClick={togglePlay}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleVideoEnded}
      >
        Your browser does not support the video element.
      </video>

      {/* Dimmed Background Overlay on Hover or Pause */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="rgba(0, 0, 0, 0.4)"
        transition="opacity 0.25s ease-in-out"
        opacity={(!isPlaying || isHovered) ? 1 : 0}
        pointerEvents="none"
        zIndex={1}
      />

      {/* Center Play/Pause Button */}
      {/* Visible if paused OR hovered */}
      <Box
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        onClick={togglePlay}
        bg="rgba(30, 41, 59, 0.75)"
        _hover={{ bg: "rgba(15, 23, 42, 0.85)", transform: "translate(-50%, -50%) scale(1.08)" }}
        transition="all 0.2s ease-in-out"
        borderRadius="14px"
        p={4}
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex={2}
        opacity={(!isPlaying || isHovered) ? 1 : 0}
        visibility={(!isPlaying || isHovered) ? 'visible' : 'hidden'}
      >
        {isPlaying ? <PauseIcon /> : <PlayIcon />}
      </Box>

      {/* Top Right Floating Controls (Fullscreen & Volume) */}
      {/* Only visible on Hover */}
      <HStack
        position="absolute"
        top="10px"
        right="10px"
        spacing="8px"
        zIndex={2}
        transition="opacity 0.25s ease-in-out, visibility 0.25s"
        opacity={isHovered ? 1 : 0}
        visibility={isHovered ? 'visible' : 'hidden'}
      >
        {/* Fullscreen Button */}
        <Box
          onClick={toggleFullscreen}
          bg="rgba(30, 41, 59, 0.75)"
          _hover={{ bg: "rgba(15, 23, 42, 0.85)" }}
          transition="all 0.2s"
          borderRadius="10px"
          p="8px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <FullscreenIcon />
        </Box>

        {/* Mute Button */}
        <Box
          onClick={toggleMute}
          bg="rgba(30, 41, 59, 0.75)"
          _hover={{ bg: "rgba(15, 23, 42, 0.85)" }}
          transition="all 0.2s"
          borderRadius="10px"
          p="8px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {isMuted ? <MuteIcon /> : <VolumeIcon />}
        </Box>
      </HStack>

      {/* Bottom Left Duration Pill */}
      {/* Only visible on Hover */}
      <Box
        position="absolute"
        bottom="12px"
        left="10px"
        bg="rgba(30, 41, 59, 0.75)"
        borderRadius="6px"
        px="8px"
        py="4px"
        zIndex={2}
        transition="opacity 0.25s ease-in-out, visibility 0.25s"
        opacity={isHovered ? 1 : 0}
        visibility={isHovered ? 'visible' : 'hidden'}
      >
        <Text color="white" fontSize="xs" fontWeight="bold" fontFamily="Inter">
          {formatTime(currentTime)}
        </Text>
      </Box>

      {/* Progress Bar Timeline at the bottom */}
      {/* Only visible on Hover */}
      <Box
        position="absolute"
        bottom="0"
        left="0"
        right="0"
        height="4px"
        bg="rgba(255, 255, 255, 0.3)"
        zIndex={2}
        transition="opacity 0.25s ease-in-out, visibility 0.25s"
        opacity={isHovered ? 1 : 0}
        visibility={isHovered ? 'visible' : 'hidden'}
      >
        <Box
          height="100%"
          bg="white"
          width={`${duration ? (currentTime / duration) * 100 : 0}%`}
          transition="width 0.1s linear"
        />
      </Box>
    </Box>
  );
};
